/* global CarouselSlider */

if (!customElements.get('product-card-image-slider')) {
  customElements.whenDefined('carousel-slider').then(() => {
    class ProductCardImageSlider extends CarouselSlider {
      constructor() {
        super();
        this.productCard = this.closest('product-card');
        this.productCardSwatches = this.productCard.querySelectorAll('.card__swatches .opt-btn');
        this.slides = this.querySelectorAll('.slider__item');

        this.productCard.addEventListener('change', this.handleSwatchChange.bind(this));
      }

      init() {
        super.init();

        this.slider.addEventListener('scroll', this.scrollInProgress.bind(this));

        // Progress bar
        this.progressContainer = this.querySelector('.carousel__progress');
        this.progressBar = this.progressContainer ? this.progressContainer.querySelector('.carousel__progress-bar') : null;

        // Touch swipe support for looping on mobile
        this._touchStartX = 0;
        this.slider.addEventListener('touchstart', (e) => {
          this._touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });

        this.slider.addEventListener('touchend', (e) => {
          if (this._isLooping) return;
          const touchEndX = e.changedTouches[0].screenX;
          const diff = this._touchStartX - touchEndX;

          if (Math.abs(diff) > 40) {
            const visibleSlides = [...this.slides].filter((s) => s.hidden !== true);
            const visibleSlideCount = visibleSlides.length;
            if (visibleSlideCount <= 1) return;

            const currentSlideIndex = Math.round(this.slider.scrollLeft / this.slideSpan);

            if (diff > 0 && currentSlideIndex >= visibleSlideCount - 1) {
              this.loopForward(visibleSlides, visibleSlideCount);
            } else if (diff < 0 && currentSlideIndex <= 0) {
              this.loopBackward(visibleSlides, visibleSlideCount);
            }
          }
        }, { passive: true });

        // If swatches are enabled, mark the swatch anchors in the slideshow
        if (this.productCardSwatches) {
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          const activeSwatchId = this.productCard.querySelector('.card__swatches .opt-btn:checked')?.dataset.mediaId || activeSlide?.dataset.mediaId;
          let activeSwatchSlide;

          if (activeSwatchId) {
            this.productCardSwatches.forEach((swatch) => {
              const swatchSlide = this.querySelector(`[data-media-id="${swatch.dataset.mediaId}"]`);
              if (swatchSlide) {
                swatchSlide.setAttribute('data-swatch-anchor', 'true');

                if (swatchSlide.dataset.mediaId === activeSwatchId) {
                  activeSlide.removeAttribute('aria-current');
                  swatchSlide.setAttribute('aria-current', 'true');
                  activeSwatchSlide = swatchSlide;
                }
              }
            });
          }

          if (activeSwatchSlide) this.handleSwatchChange(null, activeSwatchSlide.dataset.mediaId);
        } else {
          const activeSlide = this.querySelector('.slider__item[aria-current]');
          if (activeSlide) {
            this.handleSwatchChange(null, activeSlide.dataset.mediaId);
          } else {
            this.slider.slides[1]?.setAttribute('aria-current', 'true');
          }
        }

        this.updateProgressBar(0);
      }

      /**
       * Updates the progress bar width based on current slide index.
       * @param {number} index - Index of the current slide among visible slides.
       */
      updateProgressBar(index) {
        if (!this.progressBar) return;
        const visibleSlideCount = this.slides.filter((slide) => slide.hidden !== true).length;
        if (visibleSlideCount <= 0) return;
        const progress = ((index + 1) / visibleSlideCount) * 100;
        this.progressBar.style.width = `${progress}%`;
      }

      /**
       * Disables scroll-snap and CSS scroll-behavior on the slider element.
       */
      disableScrollSnap() {
        this.slider.style.scrollSnapType = 'none';
        this.slider.style.scrollBehavior = 'auto';
      }

      /**
       * Restores scroll-snap and CSS scroll-behavior on the slider element.
       */
      restoreScrollSnap() {
        this.slider.style.scrollSnapType = '';
        this.slider.style.scrollBehavior = '';
      }

      /**
       * Loops forward: clones first slide, appends it, scrolls to it, then snaps back.
       */
      loopForward(visibleSlides, visibleSlideCount) {
        this._isLooping = true;
        const firstSlide = visibleSlides[0];
        const clone = firstSlide.cloneNode(true);
        clone.classList.add('slider__item--clone');

        this.disableScrollSnap();
        this.grid.appendChild(clone);

        // Double rAF ensures the clone is fully laid out and painted before scrolling
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.slider.scrollTo({ left: clone.offsetLeft, behavior: 'smooth' });

            const cleanup = () => {
              this.slider.removeEventListener('scrollend', cleanup);
              clearTimeout(fallback);
              // Instant jump to real first slide, then remove clone
              this.slider.scrollLeft = 0;
              clone.remove();
              this.restoreScrollSnap();
              this.updateProgressBar(0);
              this._isLooping = false;
            };
            this.slider.addEventListener('scrollend', cleanup);
            const fallback = setTimeout(cleanup, 700);
          });
        });
      }

      /**
       * Loops backward: clones last slide, prepends it, scrolls to it, then snaps to real last.
       */
      loopBackward(visibleSlides, visibleSlideCount) {
        this._isLooping = true;
        const lastSlide = visibleSlides[visibleSlideCount - 1];
        const clone = lastSlide.cloneNode(true);
        clone.classList.add('slider__item--clone');

        this.disableScrollSnap();

        // Prepend clone and compensate scroll position in one synchronous block
        this.grid.insertBefore(clone, this.grid.firstChild);
        // Force reflow so the browser computes the new layout
        void this.grid.offsetHeight;
        // Compensate scroll position so visible content doesn't shift
        this.slider.scrollLeft = this.slideSpan;

        // Double rAF ensures the position adjustment is painted before starting smooth scroll
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.slider.scrollTo({ left: 0, behavior: 'smooth' });

            const cleanup = () => {
              this.slider.removeEventListener('scrollend', cleanup);
              clearTimeout(fallback);
              // Remove clone first, then instant jump to real last slide
              clone.remove();
              this.slider.scrollLeft = lastSlide.offsetLeft;
              this.restoreScrollSnap();
              this.updateProgressBar(visibleSlideCount - 1);
              this._isLooping = false;
            };
            this.slider.addEventListener('scrollend', cleanup);
            const fallback = setTimeout(cleanup, 700);
          });
        });
      }

      /**
       * Updates the visibility of slides based on the state of card swatches.
       */
      updateSlideVisibility(mediaId) {
        let hideSlide = true;
        let foundMediaId = false;
        this.slides.forEach((slide) => {
          if (!foundMediaId && slide.getAttribute('data-media-id') === mediaId) {
            hideSlide = false;
            foundMediaId = true;
          } else if (foundMediaId && slide.hasAttribute('data-swatch-anchor')) {
            hideSlide = true;
          }

          slide.toggleAttribute('hidden', hideSlide);
        });

        this.updateProgressBar(0);
        this.setButtonStates();
      }

      /**
       * Handles 'change' events in the product card swatches.
       */
      handleSwatchChange(evt, mediaId) {
        const swatchMediaId = evt?.target.dataset.mediaId || mediaId;
        if (swatchMediaId) {
          this.updateSlideVisibility(swatchMediaId);

          const variantMedia = this.querySelector(`[data-media-id="${swatchMediaId}"]`);
          if (variantMedia) {
            const left = variantMedia.closest('.slider__item').offsetLeft;
            this.slider.scrollTo({ left, behavior: 'instant' });
          }
        }
      }

      /**
       * Handles nav click with seamless same-direction looping.
       */
      handleNavClick(evt) {
        if (!evt.target.matches('.slider-nav__btn') || this._isLooping) return;

        const visibleSlides = [...this.slides].filter((slide) => slide.hidden !== true);
        const visibleSlideCount = visibleSlides.length;
        if (visibleSlideCount <= 1) return;

        const currentSlideIndex = Math.round(this.slider.scrollLeft / this.slideSpan);
        const isNext = (evt.target.name === 'next' && !this.rtl) || (evt.target.name === 'prev' && this.rtl);

        if (isNext && currentSlideIndex >= visibleSlideCount - 1) {
          this.loopForward(visibleSlides, visibleSlideCount);
        } else if (!isNext && currentSlideIndex <= 0) {
          this.loopBackward(visibleSlides, visibleSlideCount);
        } else {
          if (isNext) {
            this.slider.scrollTo({ left: this.slider.scrollLeft + this.slideSpan, behavior: 'smooth' });
          } else {
            this.slider.scrollTo({ left: this.slider.scrollLeft - this.slideSpan, behavior: 'smooth' });
          }
        }
      }

      /**
       * Never disable nav buttons (looping is enabled).
       */
      setButtonStates() {
        if (!this.prevBtn && !this.nextBtn) return;
        this.prevBtn.disabled = false;
        this.nextBtn.disabled = false;
      }

      /**
       * Handles live scroll events on the slider element.
       */
      scrollInProgress() {
        if (this._isLooping) return;
        this.slider.querySelector('[aria-current]')?.removeAttribute('aria-current');
        if (this.slideSpan) {
          const idx = Math.round(this.slider.scrollLeft / this.slideSpan);
          this.updateProgressBar(idx);
        }
      }

      /**
       * Handles debounced scroll events on the slider element.
       */
      handleScroll() {
        if (this._isLooping) return;
        super.handleScroll();
        this.slides[this.currentIndex].setAttribute('aria-current', 'true');
        this.updateProgressBar(this.currentIndex);
      }
    }

    customElements.define('product-card-image-slider', ProductCardImageSlider);
  });
}
